import {
  useRef,
  useState,
  useCallback,
  useLayoutEffect,
  useMemo,
  useEffect,
} from "react";
import { HOSTED_MODE } from "@/lib/config";
import { useSkillsEnabled } from "@/hooks/useSkillsEnabled";
import type { SkillsSource } from "@/lib/apis/mcp-skills-api";
import type {
  ChangeEvent,
  ClipboardEvent,
  DragEvent,
  FormEvent,
  KeyboardEvent,
} from "react";
import { cn } from "@/lib/chat-utils";
import { track } from "@/lib/analytics";
import { Button } from "@sphinx/design-system/button";
import { TextareaAutosize } from "@/components/ui/textarea-autosize";
import { PromptsPopover } from "@/components/chat-v2/chat-input/prompts/mcp-prompts-popover";
import {
  ArrowUp,
  Square,
  Paperclip,
  ShieldCheck,
  Plus,
  Settings2,
  Loader2,
} from "lucide-react";
import { Switch } from "@sphinx/design-system/switch";
import { FileAttachmentCard } from "@/components/chat-v2/chat-input/attachments/file-attachment-card";
import {
  type FileAttachment,
  validateFile,
  createFileAttachment,
  revokeFileAttachmentUrls,
  getFileInputAccept,
} from "@/components/chat-v2/chat-input/attachments/file-utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@sphinx/design-system/tooltip";
import { ModelSelector } from "@/components/chat-v2/chat-input/model-selector";
import {
  ClientSelector,
  type ClientSelectorData,
} from "@/components/chat-v2/chat-input/client-selector";
import { ModelDefinition, ServerFormData } from "@/shared/types";
import { AddServerModal } from "@/components/connection/AddServerModal";
import type { ServerWithName } from "@/hooks/use-app-state";
import { SystemPromptSelector } from "@/components/chat-v2/chat-input/system-prompt-selector";
import { DEFAULT_SYSTEM_PROMPT } from "@/components/chat-v2/shared/chat-helpers";
import { useTextareaCaretPosition } from "@/hooks/use-textarea-caret-position";
import {
  Context,
  ContextTrigger,
  ContextContent,
  ContextContentHeader,
  ContextContentBody,
  ContextInputUsage,
  ContextOutputUsage,
  ContextMCPServerUsage,
  ContextSystemPromptUsage,
} from "@/components/chat-v2/chat-input/context";
import {
  type MCPPromptResult,
  isMCPPromptsRequested,
} from "@/components/chat-v2/chat-input/prompts/mcp-prompts-popover";
import { MCPPromptResultCard } from "@/components/chat-v2/chat-input/prompts/mcp-prompt-result-card";
import type { SkillResult } from "@/components/chat-v2/chat-input/skills/skill-types";
import { SkillResultCard } from "@/components/chat-v2/chat-input/skills/skill-result-card";
import {
  useScenarioHostStyle,
  useScenarioHostTheme,
} from "@/contexts/scenario-client-style-context";
import { usePreferencesStore } from "@/stores/preferences/preferences-provider";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@sphinx/design-system/popover";
import { ClientStylePillSelector } from "@/components/shared/ClientStylePillSelector";
import {
  getScenarioHostFamily,
  type ScenarioHostStyle,
} from "@/lib/scenario-client-style";
type AttachmentInputSource = "picker" | "paste" | "drop";

const FILE_TRANSFER_TYPE = "Files";
const DROP_OVERLAY_TEXT = "Drop image or file to attach";

function hasFileTransfer(types: DataTransfer["types"] | readonly string[]) {
  return Array.from(types).includes(FILE_TRANSFER_TYPE);
}

function getExtensionForMediaType(mediaType: string): string {
  const extensionByMediaType: Record<string, string> = {
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/gif": "gif",
    "image/webp": "webp",
    "application/pdf": "pdf",
    "application/json": "json",
    "text/plain": "txt",
    "text/csv": "csv",
  };

  return extensionByMediaType[mediaType] ?? "bin";
}

function normalizeIncomingFile(
  file: File,
  source: AttachmentInputSource,
  index: number
): File {
  if (source !== "paste" || file.name.trim().length > 0) {
    return file;
  }

  const isImage = file.type.startsWith("image/");
  const prefix = isImage ? "pasted-image" : "pasted-file";
  const extension = getExtensionForMediaType(file.type);

  return new File([file], `${prefix}-${index + 1}.${extension}`, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

function getFilesFromClipboardData(dataTransfer: DataTransfer): File[] {
  const filesFromItems = Array.from(dataTransfer.items)
    .filter((item) => item.kind === "file")
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null);

  if (filesFromItems.length > 0) {
    return filesFromItems;
  }

  return Array.from(dataTransfer.files);
}

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (
    event: FormEvent<HTMLFormElement>,
    additionalInput?: string
  ) => void;
  stop: () => void;
  disabled?: boolean;
  submitDisabled?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  className?: string;
  currentModel: ModelDefinition;
  availableModels: ModelDefinition[];
  onModelChange: (model: ModelDefinition) => void;
  onModelSelectorOpenChange?: (open: boolean) => void;
  multiModelEnabled?: boolean;
  selectedModels?: ModelDefinition[];
  onSelectedModelsChange?: (models: ModelDefinition[]) => void;
  onMultiModelEnabledChange?: (enabled: boolean) => void;
  enableMultiModel?: boolean;
  /** Playground-only: renders a client chip beside the model chip. */
  clientSelector?: ClientSelectorData;
  /**
   * Hide the composer's model picker. The Playground moved model choice to
   * the top of the surface (column headers + the direct chip) — the bottom
   * picker said the same thing twice. Compare mode keeps it: the multi-model
   * picker IS how compare columns are chosen.
   */
  hideModelSelector?: boolean;
  /** Hide the client lineup picker — set once a session is active. */
  hideClientSelector?: boolean;
  systemPrompt: string;
  onSystemPromptChange: (prompt: string) => void;
  temperature: number;
  onTemperatureChange: (temperature: number) => void;
  hasMessages?: boolean;
  onResetChat: () => void;
  tokenUsage?: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  };
  selectedServers?: string[];
  mcpToolsTokenCount?: Record<string, number> | null;
  mcpToolsTokenCountErrors?: Record<string, string> | null;
  mcpToolsTokenCountLoading?: boolean;
  connectedOrConnectingServerConfigs?: Record<string, { name: string }>;
  systemPromptTokenCount?: number | null;
  systemPromptTokenCountLoading?: boolean;
  mcpPromptResults: MCPPromptResult[];
  onChangeMcpPromptResults: (mcpPromptResults: MCPPromptResult[]) => void;
  skillResults: SkillResult[];
  onChangeSkillResults: (skillResults: SkillResult[]) => void;
  /** File attachments for the message */
  fileAttachments?: FileAttachment[];
  /** Callback when file attachments change */
  onChangeFileAttachments?: (files: FileAttachment[]) => void;
  /** Tool approval toggle */
  requireToolApproval?: boolean;
  onRequireToolApprovalChange?: (enabled: boolean) => void;
  /** Shared chat-only mode */
  minimalMode?: boolean;
  /** Allow the context popover while the surrounding surface is minimal. */
  showContextPopover?: boolean;
  /** Main chat: show the Claude/ChatGPT host-style selector in the "+" menu. */
  showHostStyleSelector?: boolean;
  /** Current host style for the selector UI. */
  hostStyle?: ScenarioHostStyle;
  /** Shared host-style setter. */
  onHostStyleChange?: (hostStyle: ScenarioHostStyle) => void;
  /** Onboarding: pulse the send button with glow animation */
  pulseSubmit?: boolean;
  /** Move the textarea caret to the end when this trigger changes */
  moveCaretToEndTrigger?: number;
  /** All project servers for the "+" dropdown server toggles. */
  allServerConfigs?: Record<string, ServerWithName>;
  /**
   * @deprecated Connectivity is now the single source of truth — the popover
   * toggle connects/disconnects via `onDisconnectServer`/`onReconnectServer`
   * rather than maintaining a separate per-chat selection. Kept so existing
   * callers compile; no longer read here.
   */
  onServerToggle?: (serverName: string) => void;
  /** Reconnect a disconnected server. */
  onReconnectServer?: (serverName: string) => Promise<void>;
  /**
   * Disconnect a connected server. Connectivity is the single source of
   * truth for which servers the Playground uses, so toggling a server off
   * here unplugs it (it stays in the list as a "Connect" row).
   */
  onDisconnectServer?: (serverName: string) => void;
  /** Add a new server (opens the add-server modal). */
  onAddServer?: (formData: ServerFormData) => void;
  /** Hosted scenario: optional servers not yet connected (Add server popover). */
  scenarioAttachableServers?: Array<{
    serverId: string;
    serverName: string;
    useOAuth: boolean;
  }>;
  onAttachScenarioServer?: (serverId: string) => void;
  /**
   * Environment mode (Project Environments): the environment's resolved
   * servers, id-first from the preview. When present (even empty) this
   * REPLACES the ad-hoc rows above in the "+" menu — environment servers are
   * connected by the backend on every turn, so there is no browser connection
   * to manage and a "Connect" button would be a dead control. The toggle is
   * the per-turn narrowing override, not a connect/disconnect.
   */
  environmentServers?: Array<{
    serverId: string;
    name: string;
    enabled: boolean;
    source?: string | null;
  }>;
  onEnvironmentServerToggle?: (serverId: string, enabled: boolean) => void;
  /**
   * True when the toggles above hold a per-turn override (some server was
   * turned off/on for this session). Renders the Modified marker + reset —
   * this menu is the ONLY surface for the override now, so the honesty
   * affordance lives here too.
   */
  environmentServersOverridden?: boolean;
  onResetEnvironmentServers?: () => void;
}

export function ChatInput({
  value,
  onChange,
  onSubmit,
  stop,
  disabled = false,
  submitDisabled = false,
  isLoading = false,
  placeholder = "Type your message...",
  className,
  currentModel,
  availableModels,
  onModelChange,
  onModelSelectorOpenChange,
  hideModelSelector = false,
  hideClientSelector = false,
  multiModelEnabled = false,
  selectedModels,
  onSelectedModelsChange,
  onMultiModelEnabledChange,
  enableMultiModel = false,
  clientSelector,
  systemPrompt,
  onSystemPromptChange,
  temperature,
  onTemperatureChange,
  onResetChat,
  hasMessages = false,
  tokenUsage,
  selectedServers,
  mcpToolsTokenCount,
  mcpToolsTokenCountErrors,
  mcpToolsTokenCountLoading = false,
  connectedOrConnectingServerConfigs,
  systemPromptTokenCount,
  systemPromptTokenCountLoading = false,
  mcpPromptResults,
  onChangeMcpPromptResults,
  skillResults,
  onChangeSkillResults,
  fileAttachments = [],
  onChangeFileAttachments,
  requireToolApproval = false,
  onRequireToolApprovalChange,
  minimalMode = false,
  showContextPopover,
  showHostStyleSelector = false,
  hostStyle,
  onHostStyleChange,
  pulseSubmit = false,
  moveCaretToEndTrigger,
  allServerConfigs,
  onReconnectServer,
  onDisconnectServer,
  onAddServer,
  scenarioAttachableServers,
  onAttachScenarioServer,
  environmentServers,
  onEnvironmentServerToggle,
  environmentServersOverridden = false,
  onResetEnvironmentServers,
}: ChatInputProps) {
  // Cloud skill source for the `/` picker: in hosted mode, list/load skills
  // from the project's Convex/Computer source (Playground carries projectId via
  // `clientSelector`). Gated behind the `skills-enabled` flag until QA completes
  // (flag off ⇒ no cloud source, so the picker lists no cloud skills). Local
  // mode keeps the default (filesystem) path. Memoized so the popover's fetch
  // effects don't re-run every render.
  const skillsEnabled = useSkillsEnabled();
  // Skills over MCP (SEP-2640): the selected servers ARE the candidate
  // providers. `connected: true` because a server only reaches
  // `selectedServers` once it is attached to this turn; the API still answers
  // `support.active: false` for any connection where the extension is not
  // mutually declared, so a non-declaring server contributes nothing either
  // way.
  //
  // MEMOIZED on a stable signature. Rebuilding the array every render would
  // give the consuming effect a fresh reference each pass, and since that
  // effect fetches, every completed fetch would trigger the next one.
  // JSON, not concatenation: a server id may contain the separator, and two
  // different selections that concatenate alike would reuse a stale array.
  const selectedServersSignature = JSON.stringify(selectedServers ?? []);
  const serverSkillProviders = useMemo(
    () =>
      (selectedServers ?? []).map((serverId) => ({
        serverId,
        label: serverId,
        connected: true,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedServersSignature]
  );

  const skillsSource = useMemo<SkillsSource | undefined>(
    () =>
      HOSTED_MODE && skillsEnabled && clientSelector?.cloudProjectId
        ? { kind: "cloud", projectId: clientSelector.cloudProjectId }
        : undefined,
    [clientSelector?.cloudProjectId, skillsEnabled]
  );
  const scenarioHostStyle = useScenarioHostStyle();
  const scenarioHostTheme = useScenarioHostTheme();
  const globalThemeMode = usePreferencesStore((s) => s.themeMode);
  const resolvedThemeMode = scenarioHostTheme ?? globalThemeMode;
  const isDarkScenarioTheme = resolvedThemeMode === "dark";
  const formRef = useRef<HTMLFormElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef(true);
  const valueRef = useRef(value);
  const [caretIndex, setCaretIndex] = useState(0);
  const [mcpPromptPopoverKeyTrigger, setMcpPromptPopoverKeyTrigger] = useState<
    string | null
  >(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileDragDepth, setFileDragDepth] = useState(0);
  const [plusPopoverOpen, setPlusPopoverOpen] = useState(false);
  const handlePlusPopoverOpenChange = (nextOpen: boolean) => {
    if (nextOpen && !plusPopoverOpen) {
      track("chat_options_plus_clicked", { location: "chat_input" });
    }
    setPlusPopoverOpen(nextOpen);
  };
  const [addServerModalOpen, setAddServerModalOpen] = useState(false);
  const [systemPromptOpen, setSystemPromptOpen] = useState(false);
  const selectorHostStyle = hostStyle ?? scenarioHostStyle;
  const hasServerRows = Boolean(
    allServerConfigs &&
      onDisconnectServer &&
      Object.keys(allServerConfigs).length > 0
  );
  // Environment mode replaces the ad-hoc section outright — presence of the
  // prop (not its length) is the mode switch, so an environment that resolves
  // to zero servers shows no dead "Add server"/Connect controls either.
  //
  // `environmentServersOverridden` keeps the section even at zero servers: an
  // override survives live edits of the environment (by design), so an
  // environment edited down to zero servers can still carry a live override —
  // a retained id can still run if it's an authorized project server. Hiding
  // the section then would hide the Modified marker AND the only way to reset.
  const isEnvironmentServerMode = environmentServers !== undefined;
  const environmentSectionVisible =
    isEnvironmentServerMode &&
    (environmentServers.length > 0 || environmentServersOverridden);
  const hasServerOptions = isEnvironmentServerMode
    ? environmentSectionVisible
    : Boolean(onAddServer || hasServerRows);
  const showHostStyleSelectorControl =
    showHostStyleSelector &&
    Boolean(selectorHostStyle) &&
    Boolean(onHostStyleChange);

  const caret = useTextareaCaretPosition(
    textareaRef,
    containerRef,
    value,
    caretIndex
  );

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useLayoutEffect(() => {
    if (moveCaretToEndTrigger === undefined) return;

    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.focus();
    const end = textarea.value.length;
    textarea.setSelectionRange(end, end);
    setCaretIndex(end);
  }, [moveCaretToEndTrigger]);

  const onMCPPromptSelected = useCallback(
    (mcpPromptResult: MCPPromptResult) => {
      // Add the prompt result to the mcpPromptResults state
      onChangeMcpPromptResults([...mcpPromptResults, mcpPromptResult]);

      // Remove the "/" that triggered the popover
      const textBeforeCaret = value.slice(0, caretIndex);
      const textAfterCaret = value.slice(caretIndex);
      const cleanedBefore = textBeforeCaret.replace(/\/\s*$/, "");
      const newValue = cleanedBefore + textAfterCaret;
      onChange(newValue);
    },
    [value, caretIndex, onChange, mcpPromptResults, onChangeMcpPromptResults]
  );

  const removeMCPPromptResult = (index: number) => {
    onChangeMcpPromptResults(mcpPromptResults.filter((_, i) => i !== index));
  };

  const canHandleFileTransfers = Boolean(onChangeFileAttachments);
  const canAttachFiles = canHandleFileTransfers && !disabled;
  const isFileDragActive = fileDragDepth > 0;

  // File attachment handlers
  const addFileAttachments = useCallback(
    (files: Iterable<File>, source: AttachmentInputSource) => {
      if (!onChangeFileAttachments) return false;

      const incomingFiles = Array.from(files).map((file, index) =>
        normalizeIncomingFile(file, source, index)
      );
      if (incomingFiles.length === 0) return false;

      setFileError(null);
      const newAttachments: FileAttachment[] = [];
      const errors: string[] = [];

      for (const file of incomingFiles) {
        const validation = validateFile(file);

        if (validation.valid) {
          newAttachments.push(createFileAttachment(file));
        } else {
          errors.push(`${file.name}: ${validation.error}`);
        }
      }

      if (newAttachments.length > 0) {
        onChangeFileAttachments([...fileAttachments, ...newAttachments]);
      }

      if (errors.length > 0) {
        setFileError(errors.join("\n"));
        // Clear error after 5 seconds
        setTimeout(() => setFileError(null), 5000);
      }

      return true;
    },
    [fileAttachments, onChangeFileAttachments]
  );

  const handleFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      addFileAttachments(Array.from(files), "picker");

      // Reset input so the same file can be selected again
      event.target.value = "";
    },
    [addFileAttachments]
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLTextAreaElement>) => {
      if (!canAttachFiles) return;

      const files = getFilesFromClipboardData(event.clipboardData);
      if (files.length === 0) return;

      event.preventDefault();
      addFileAttachments(files, "paste");
      textareaRef.current?.focus();
    },
    [addFileAttachments, canAttachFiles]
  );

  const handleDragEnter = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (!canHandleFileTransfers || !hasFileTransfer(event.dataTransfer.types))
        return;

      event.preventDefault();
      event.stopPropagation();
      if (disabled) return;

      setFileDragDepth((depth) => depth + 1);
    },
    [canHandleFileTransfers, disabled]
  );

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (!canHandleFileTransfers || !hasFileTransfer(event.dataTransfer.types))
        return;

      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = disabled ? "none" : "copy";
    },
    [canHandleFileTransfers, disabled]
  );

  const handleDragLeave = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (!canHandleFileTransfers || !hasFileTransfer(event.dataTransfer.types))
        return;

      event.preventDefault();
      event.stopPropagation();
      if (disabled) return;

      setFileDragDepth((depth) => Math.max(0, depth - 1));
    },
    [canHandleFileTransfers, disabled]
  );

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (!canHandleFileTransfers || !hasFileTransfer(event.dataTransfer.types))
        return;

      event.preventDefault();
      event.stopPropagation();
      setFileDragDepth(0);
      if (disabled) return;

      addFileAttachments(Array.from(event.dataTransfer.files), "drop");
      textareaRef.current?.focus();
    },
    [addFileAttachments, canHandleFileTransfers, disabled]
  );

  const removeFileAttachment = useCallback(
    (id: string) => {
      if (!onChangeFileAttachments) return;

      const attachment = fileAttachments.find((a) => a.id === id);
      if (attachment) {
        revokeFileAttachmentUrls([attachment]);
      }

      onChangeFileAttachments(fileAttachments.filter((a) => a.id !== id));
    },
    [fileAttachments, onChangeFileAttachments]
  );

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onSkillSelected = useCallback(
    (skillResult: SkillResult) => {
      // Add the skill result to the skillResults state
      onChangeSkillResults([...skillResults, skillResult]);

      // Remove the "/" that triggered the popover
      const textBeforeCaret = value.slice(0, caretIndex);
      const textAfterCaret = value.slice(caretIndex);
      const cleanedBefore = textBeforeCaret.replace(/\/\s*$/, "");
      const newValue = cleanedBefore + textAfterCaret;
      onChange(newValue);
    },
    [value, caretIndex, onChange, skillResults, onChangeSkillResults]
  );

  const removeSkillResult = (index: number) => {
    onChangeSkillResults(skillResults.filter((_, i) => i !== index));
  };

  // Check if there are any results (prompts, skills, or files) selected
  const hasResults =
    mcpPromptResults.length > 0 ||
    skillResults.length > 0 ||
    fileAttachments.length > 0;
  const effectiveSelectedModels =
    selectedModels && selectedModels.length > 0
      ? selectedModels
      : [currentModel];
  const hideContextPopover = multiModelEnabled;
  const contextPopoverVisible = showContextPopover ?? !minimalMode;

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    const currentCaretIndex = event.currentTarget.selectionStart;
    if (
      isMCPPromptsRequested(value, currentCaretIndex) &&
      ["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(event.key)
    ) {
      event.preventDefault();
      setMcpPromptPopoverKeyTrigger(event.key);
      return;
    }

    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      const trimmed = value.trim();
      event.preventDefault();
      if (
        (!trimmed && !hasResults) ||
        disabled ||
        submitDisabled ||
        isLoading
      ) {
        return;
      }
      formRef.current?.requestSubmit();
    }
  };

  const renderResultCards = () => {
    if (!hasResults) return null;
    return (
      <div className="px-4 pt-1 pb-0.5">
        <div className="flex flex-wrap gap-1.5">
          {mcpPromptResults.map((mcpPromptResult, index) => (
            <MCPPromptResultCard
              key={`prompt-${index}`}
              mcpPromptResult={mcpPromptResult}
              onRemove={() => removeMCPPromptResult(index)}
            />
          ))}
          {skillResults.map((skillResult, index) => (
            <SkillResultCard
              key={`skill-${index}`}
              skillResult={skillResult}
              skillsSource={skillsSource}
              onRemove={() => removeSkillResult(index)}
              onUpdate={(updatedSkill) => {
                const newSkillResults = [...skillResults];
                newSkillResults[index] = updatedSkill;
                onChangeSkillResults(newSkillResults);
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderFileAttachmentCards = () => {
    if (fileAttachments.length === 0) return null;
    return (
      <div className="px-4 pt-1 pb-0.5">
        <div className="flex flex-wrap gap-1.5">
          {fileAttachments.map((attachment) => (
            <FileAttachmentCard
              key={attachment.id}
              attachment={attachment}
              onRemove={() => removeFileAttachment(attachment.id)}
            />
          ))}
        </div>
      </div>
    );
  };

  const scenarioHostFamily = getScenarioHostFamily(scenarioHostStyle);
  const composerClasses =
    scenarioHostFamily === "chatgpt"
      ? cn(
          "scenario-host-composer rounded-[1.75rem]",
          isDarkScenarioTheme
            ? "border border-white/10 bg-[#303030] shadow-[0_1px_2px_rgba(0,0,0,0.28),0_4px_24px_rgba(130,130,130,0.14)]"
            : "border border-neutral-200/90 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_22px_rgba(100,100,100,0.08)]"
        )
      : scenarioHostFamily === "claude"
      ? cn(
          "scenario-host-composer rounded-[1.35rem]",
          isDarkScenarioTheme
            ? "border-[#4b463d] bg-[#30302E] shadow-[0_1px_2px_rgba(0,0,0,0.28),0_4px_22px_rgba(120,120,120,0.12)]"
            : "border border-[#DFDFDB] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.05),0_4px_20px_rgba(110,110,110,0.08)]"
        )
      : "rounded-3xl border border-border/40 bg-muted/70";
  const activeSubmitButtonClasses =
    scenarioHostFamily === "chatgpt"
      ? isDarkScenarioTheme
        ? "bg-[#f4f4f4] text-[#1f1f1f] hover:bg-[#e8e8e8]"
        : "bg-[#1f1f1f] text-white hover:bg-[#303030]"
      : scenarioHostFamily === "claude"
      ? isDarkScenarioTheme
        ? "bg-[#d07b53] text-[#fff7f0] hover:bg-[#c06f49]"
        : "bg-[#e27d47] text-white hover:bg-[#d16f3d]"
      : "bg-primary text-primary-foreground hover:bg-primary/90";
  const inactiveSubmitButtonClasses =
    scenarioHostFamily === "chatgpt"
      ? isDarkScenarioTheme
        ? "bg-[#3a3a3a] text-[#8a8a8a] cursor-not-allowed"
        : "bg-[#e7e7e7] text-[#9b9b9b] cursor-not-allowed"
      : scenarioHostFamily === "claude"
      ? isDarkScenarioTheme
        ? "bg-[#45413b] text-[#8d857a] cursor-not-allowed"
        : "bg-[#ebe5dc] text-[#b6ada0] cursor-not-allowed"
      : "bg-muted text-muted-foreground cursor-not-allowed";

  return (
    <>
      <form
        ref={formRef}
        className={cn("w-full", className)}
        onSubmit={onSubmit}
      >
        <div
          ref={containerRef}
          data-testid="chat-input-composer"
          className={cn(
            "relative flex w-full flex-col px-2 pt-2 pb-2",
            isFileDragActive &&
              "ring-2 ring-primary/45 ring-offset-2 ring-offset-background",
            composerClasses
          )}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isFileDragActive && (
            <div
              className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-[inherit] bg-background/85 px-4 text-center text-sm font-medium text-foreground shadow-inner backdrop-blur-sm"
              role="status"
              aria-live="polite"
            >
              {DROP_OVERLAY_TEXT}
            </div>
          )}

          <PromptsPopover
            anchor={caret}
            selectedServers={selectedServers}
            onPromptSelected={onMCPPromptSelected}
            onSkillSelected={onSkillSelected}
            actionTrigger={mcpPromptPopoverKeyTrigger}
            setActionTrigger={setMcpPromptPopoverKeyTrigger}
            value={value}
            caretIndex={caretIndex}
            minimalMode={minimalMode}
            skillsSource={skillsSource}
            // Skills over MCP (SEP-2640): the selected servers ARE the
            // candidate providers. `connected: true` because a server only
            // reaches `selectedServers` once it is attached to this turn; the
            // API still answers `support.active: false` for any connection
            // where the extension is not mutually declared, so a
            // non-declaring server contributes nothing either way.
            mcpServers={serverSkillProviders}
            {...(skillsSource?.kind === "cloud"
              ? { projectId: skillsSource.projectId }
              : {})}
          />

          {minimalMode &&
          scenarioAttachableServers &&
          scenarioAttachableServers.length > 0 &&
          onAttachScenarioServer ? (
            <div className="flex flex-wrap items-center gap-2 px-4 pb-1 pt-0.5">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1 rounded-full border-dashed px-3 text-xs"
                    disabled={disabled}
                    aria-label="Add optional connector"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add connector
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-1" align="start">
                  <p className="px-2 py-1.5 text-xs text-muted-foreground">
                    Connect an optional server. You may be asked to authorize.
                  </p>
                  <div className="max-h-48 overflow-y-auto">
                    {scenarioAttachableServers.map((s) => (
                      <button
                        key={s.serverId}
                        type="button"
                        className="flex w-full items-center justify-between rounded-md px-2 py-2 text-left text-sm hover:bg-muted/80"
                        onClick={() => onAttachScenarioServer(s.serverId)}
                      >
                        <span className="truncate font-medium">
                          {s.serverName}
                        </span>
                        {s.useOAuth ? (
                          <span className="shrink-0 text-[10px] text-muted-foreground">
                            OAuth
                          </span>
                        ) : null}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          ) : null}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={getFileInputAccept()}
            onChange={handleFileInputChange}
            className="hidden"
            aria-hidden="true"
          />

          {/* File Attachment Cards */}
          {renderFileAttachmentCards()}

          {/* MCP Prompts and Skills Cards */}
          {renderResultCards()}

          {/* File validation error */}
          {fileError && (
            <div className="px-4 py-1">
              <p className="text-xs text-destructive whitespace-pre-line">
                {fileError}
              </p>
            </div>
          )}

          <TextareaAutosize
            ref={textareaRef}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              setCaretIndex(e.target.selectionStart);
            }}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            onKeyUp={(e) => setCaretIndex(e.currentTarget.selectionStart)}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={disabled}
            minRows={2}
            maxRows={4}
            className={cn(
              "min-h-[64px] w-full resize-none overflow-y-auto overscroll-contain border-none bg-transparent dark:bg-transparent px-4",
              "pt-2 pb-3 text-base text-foreground placeholder:text-muted-foreground/70",
              "outline-none focus-visible:outline-none focus-visible:ring-0 shadow-none focus-visible:shadow-none",
              disabled ? "cursor-not-allowed text-muted-foreground" : ""
            )}
            autoFocus={!disabled}
          />

          <div className="@container/toolbar flex items-center justify-between gap-2 px-2 min-w-0">
            <div className="flex items-center gap-1 min-w-0 flex-shrink overflow-hidden">
              {!minimalMode && (
                <Popover
                  open={plusPopoverOpen}
                  onOpenChange={handlePlusPopoverOpenChange}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-full"
                          disabled={disabled}
                          aria-label="Options"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                    </TooltipTrigger>
                    <TooltipContent>Options</TooltipContent>
                  </Tooltip>
                  <PopoverContent
                    className="w-72 p-0"
                    align="start"
                    side="top"
                    sideOffset={8}
                  >
                    {hasServerOptions && (
                      <div className="px-1 pt-1 pb-0">
                        <p className="px-2 py-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                          {isEnvironmentServerMode
                            ? "Environment connectors"
                            : "Connectors"}
                        </p>
                        {isEnvironmentServerMode && (
                          <>
                            <p className="px-2 pb-1.5 text-[11px] text-muted-foreground">
                              Connected automatically on every message.
                            </p>
                            {environmentServersOverridden && (
                              <div className="flex items-center gap-1.5 px-2 pb-1.5">
                                <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                                  Modified
                                </span>
                                {onResetEnvironmentServers && (
                                  <button
                                    type="button"
                                    onClick={onResetEnvironmentServers}
                                    // Same mid-turn lock as the toggles below.
                                    disabled={isLoading}
                                    className="text-[11px] text-primary underline-offset-4 hover:underline disabled:opacity-60"
                                  >
                                    Reset to environment
                                  </button>
                                )}
                              </div>
                            )}
                            <div className="max-h-48 overflow-y-auto">
                              {environmentServers.map((server) => (
                                <div
                                  key={server.serverId}
                                  className="flex items-center justify-between gap-2 rounded-md px-2 py-2 hover:bg-muted/60"
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <div
                                      className={cn(
                                        "w-2 h-2 rounded-full shrink-0",
                                        server.enabled
                                          ? "bg-green-500 dark:bg-green-400"
                                          : "bg-muted-foreground"
                                      )}
                                    />
                                    <span
                                      className={cn(
                                        "text-sm font-medium truncate",
                                        !server.enabled &&
                                          "text-muted-foreground"
                                      )}
                                    >
                                      {server.name}
                                    </span>
                                    {server.source === "plugin" && (
                                      <span className="text-[10px] text-muted-foreground shrink-0 uppercase">
                                        plugin
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center shrink-0">
                                    <Switch
                                      checked={server.enabled}
                                      onCheckedChange={(next) =>
                                        onEnvironmentServerToggle?.(
                                          server.serverId,
                                          next === true
                                        )
                                      }
                                      // Locked while a turn is in flight —
                                      // the same guard the header section
                                      // applied before these controls moved:
                                      // the running turn keeps its resolved
                                      // set, so a mid-stream flip would only
                                      // change NEXT turn while looking like
                                      // it changed this one.
                                      disabled={isLoading}
                                      aria-label={`Include ${server.name}`}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                        {!isEnvironmentServerMode &&
                          allServerConfigs &&
                          onDisconnectServer &&
                          Object.keys(allServerConfigs).length > 0 && (
                            <div className="max-h-48 overflow-y-auto">
                              {Object.entries(allServerConfigs)
                                .sort(([aName, a], [bName, b]) => {
                                  const statusOrder: Record<string, number> = {
                                    connected: 0,
                                    connecting: 1,
                                    failed: 2,
                                  };
                                  const aOrder =
                                    statusOrder[a.connectionStatus] ?? 3;
                                  const bOrder =
                                    statusOrder[b.connectionStatus] ?? 3;
                                  if (aOrder !== bOrder) return aOrder - bOrder;
                                  return aName.localeCompare(bName);
                                })
                                .map(([name, server]) => {
                                  const isConnected =
                                    server.connectionStatus === "connected";
                                  const isConnecting =
                                    server.connectionStatus === "connecting";
                                  const isFailed =
                                    server.connectionStatus === "failed";
                                  const statusColor = isConnected
                                    ? "bg-green-500 dark:bg-green-400"
                                    : isConnecting
                                    ? "bg-yellow-500 dark:bg-yellow-400 animate-pulse"
                                    : isFailed
                                    ? "bg-red-500 dark:bg-red-400"
                                    : "bg-muted-foreground";

                                  return (
                                    <div
                                      key={name}
                                      className="flex items-center justify-between gap-2 rounded-md px-2 py-2 hover:bg-muted/60"
                                    >
                                      <div className="flex items-center gap-2 min-w-0">
                                        <div
                                          className={cn(
                                            "w-2 h-2 rounded-full shrink-0",
                                            statusColor
                                          )}
                                        />
                                        <span
                                          className={cn(
                                            "text-sm font-medium truncate",
                                            !isConnected &&
                                              !isConnecting &&
                                              "text-muted-foreground"
                                          )}
                                        >
                                          {name}
                                        </span>
                                        <span className="text-[10px] text-muted-foreground shrink-0">
                                          {server.config.command
                                            ? "STDIO"
                                            : "HTTP"}
                                        </span>
                                      </div>
                                      <div className="flex items-center shrink-0">
                                        {isConnecting ? (
                                          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                                        ) : isConnected ? (
                                          // Connectivity is the source of truth:
                                          // a connected server is "on", and
                                          // toggling off disconnects it.
                                          <Switch
                                            checked={true}
                                            onCheckedChange={() =>
                                              onDisconnectServer?.(name)
                                            }
                                          />
                                        ) : (
                                          <button
                                            type="button"
                                            className="text-xs font-medium text-primary hover:text-primary/80 transition-colors cursor-pointer px-1.5 py-0.5 rounded hover:bg-primary/5"
                                            onClick={() => {
                                              onReconnectServer?.(name).catch(
                                                () => {}
                                              );
                                            }}
                                          >
                                            {isFailed ? "Retry" : "Connect"}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          )}
                        {!isEnvironmentServerMode && onAddServer && (
                          <button
                            type="button"
                            className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/60 cursor-pointer"
                            onClick={() => {
                              setPlusPopoverOpen(false);
                              setAddServerModalOpen(true);
                            }}
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add connector
                          </button>
                        )}
                      </div>
                    )}

                    <div
                      className={cn(
                        "px-1 pb-1",
                        (isEnvironmentServerMode
                          ? environmentSectionVisible
                          : allServerConfigs &&
                            Object.keys(allServerConfigs).length > 0) &&
                          "border-t border-border mt-1 pt-1"
                      )}
                    >
                      {onChangeFileAttachments && (
                        <button
                          type="button"
                          className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-muted/60 cursor-pointer"
                          onClick={() => {
                            track("chat_attachment_button_clicked", {
                              location: "chat_input",
                            });
                            setPlusPopoverOpen(false);
                            openFilePicker();
                          }}
                        >
                          <Paperclip className="h-4 w-4 text-muted-foreground" />
                          Attach files
                        </button>
                      )}

                      <button
                        type="button"
                        className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-muted/60 cursor-pointer"
                        onClick={() => {
                          setPlusPopoverOpen(false);
                          setSystemPromptOpen(true);
                        }}
                      >
                        <Settings2 className="h-4 w-4 text-muted-foreground" />
                        System Prompt & Temperature
                      </button>

                      {onRequireToolApprovalChange && (
                        <div className="flex items-center justify-between gap-2 rounded-md px-2 py-2 hover:bg-muted/60">
                          <div className="flex items-center gap-2 text-sm">
                            <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                            Tool Approval
                          </div>
                          <Switch
                            checked={requireToolApproval}
                            onCheckedChange={(checked) =>
                              onRequireToolApprovalChange(checked)
                            }
                          />
                        </div>
                      )}

                      {showHostStyleSelectorControl && selectorHostStyle && (
                        <div className="mt-1 border-t border-border/70 px-2 py-[5px]">
                          <div className="flex items-center justify-between gap-2">
                            <p className="shrink-0 text-[9px] font-medium text-muted-foreground uppercase tracking-[0.18em]">
                              Client Style
                            </p>
                            <ClientStylePillSelector
                              className="w-[164px] shrink-0"
                              value={selectorHostStyle}
                              onValueChange={(nextStyle) =>
                                onHostStyleChange?.(nextStyle)
                              }
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              {!minimalMode && !hideClientSelector && clientSelector ? (
                <ClientSelector
                  {...clientSelector}
                  isLoading={isLoading}
                  onOpenChange={onModelSelectorOpenChange}
                  themeMode={resolvedThemeMode}
                  modalThemeMode={globalThemeMode}
                />
              ) : null}
              {!minimalMode && !hideModelSelector && (
                <ModelSelector
                  currentModel={currentModel}
                  availableModels={availableModels}
                  onModelChange={onModelChange}
                  onOpenChange={onModelSelectorOpenChange}
                  isLoading={isLoading}
                  hasMessages={hasMessages}
                  enableMultiModel={enableMultiModel}
                  multiModelEnabled={multiModelEnabled}
                  selectedModels={effectiveSelectedModels}
                  onSelectedModelsChange={onSelectedModelsChange}
                  onMultiModelEnabledChange={onMultiModelEnabledChange}
                />
              )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {contextPopoverVisible && !hideContextPopover && (
                <Context
                  usedTokens={tokenUsage?.totalTokens ?? 0}
                  usage={
                    tokenUsage && tokenUsage.totalTokens > 0
                      ? {
                          inputTokens: tokenUsage.inputTokens,
                          outputTokens: tokenUsage.outputTokens,
                          totalTokens: tokenUsage.totalTokens,
                          inputTokenDetails: {
                            noCacheTokens: undefined,
                            cacheReadTokens: undefined,
                            cacheWriteTokens: undefined,
                          },
                          outputTokenDetails: {
                            textTokens: undefined,
                            reasoningTokens: undefined,
                          },
                        }
                      : undefined
                  }
                  modelId={`${currentModel.id}`}
                  selectedServers={selectedServers}
                  mcpToolsTokenCount={mcpToolsTokenCount}
                  mcpToolsTokenCountErrors={mcpToolsTokenCountErrors}
                  mcpToolsTokenCountLoading={mcpToolsTokenCountLoading}
                  connectedOrConnectingServerConfigs={
                    connectedOrConnectingServerConfigs
                  }
                  systemPromptTokenCount={systemPromptTokenCount}
                  systemPromptTokenCountLoading={
                    systemPromptTokenCountLoading
                  }
                  hasMessages={hasMessages}
                >
                  <ContextTrigger />
                  {/* Only render popover content when there's something to show */}
                  {(hasMessages &&
                    tokenUsage &&
                    tokenUsage.totalTokens > 0) ||
                  (systemPromptTokenCount && systemPromptTokenCount > 0) ||
                  systemPromptTokenCountLoading ||
                  (mcpToolsTokenCount &&
                    Object.keys(mcpToolsTokenCount).length > 0) ||
                  (mcpToolsTokenCountErrors &&
                    Object.keys(mcpToolsTokenCountErrors).length > 0) ||
                  mcpToolsTokenCountLoading ? (
                    <ContextContent>
                      {hasMessages &&
                        tokenUsage &&
                        tokenUsage.totalTokens > 0 && (
                          <ContextContentHeader />
                        )}
                      <ContextContentBody>
                        {hasMessages &&
                          tokenUsage &&
                          tokenUsage.totalTokens > 0 && (
                            <>
                              <ContextInputUsage />
                              <ContextOutputUsage />
                            </>
                          )}
                        <ContextSystemPromptUsage />
                        <ContextMCPServerUsage />
                      </ContextContentBody>
                    </ContextContent>
                  ) : null}
                </Context>
              )}
              {isLoading ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant="secondary"
                      className="size-[34px] rounded-full transition-colors"
                      aria-label="Stop generating"
                      onClick={() => stop()}
                    >
                      <Square size={16} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Stop generating</TooltipContent>
                </Tooltip>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="submit"
                      size="icon"
                      aria-label="Send message"
                      className={cn(
                        "size-[34px] rounded-full transition-colors shadow-none",
                        (value.trim() || hasResults) &&
                          !disabled &&
                          !submitDisabled
                          ? activeSubmitButtonClasses
                          : inactiveSubmitButtonClasses,
                        pulseSubmit && "animate-onboarding-pulse"
                      )}
                      disabled={
                        (!value.trim() && !hasResults) ||
                        disabled ||
                        submitDisabled
                      }
                    >
                      <ArrowUp size={16} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Send message</TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </form>

      {onAddServer && (
        <AddServerModal
          isOpen={addServerModalOpen}
          onClose={() => setAddServerModalOpen(false)}
          onSubmit={(formData) => {
            onAddServer(formData);
            setAddServerModalOpen(false);
          }}
        />
      )}

      {!minimalMode && (
        <SystemPromptSelector
          systemPrompt={systemPrompt || DEFAULT_SYSTEM_PROMPT}
          onSystemPromptChange={onSystemPromptChange}
          temperature={temperature}
          onTemperatureChange={onTemperatureChange}
          isLoading={isLoading}
          hasMessages={hasMessages}
          onResetChat={onResetChat}
          currentModel={currentModel}
          multiModelEnabled={multiModelEnabled}
          selectedModels={effectiveSelectedModels}
          open={systemPromptOpen}
          onOpenChange={setSystemPromptOpen}
        />
      )}
    </>
  );
}
